import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Label,
  Button,
  Spinner,
  Alert,
  Navbar,
  NavbarBrand,
} from "reactstrap";
import { Formik, Field, FieldArray, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axiosInstance from "Axiosinstance";

const branchSchema = Yup.object().shape({
  branch_name: Yup.string()
    .trim()
    .max(30, "Branch name must be 30 characters or fewer")
    .required("Branch name is required"),
  branch_address: Yup.string()
    .trim()
    .max(200, "Branch address must be 200 characters or fewer")
    .required("Branch address is required"),
  branch_state: Yup.string()
    .trim()
    .max(20, "Branch state must be 20 characters or fewer")
    .required("Branch state is required"),
  branch_gst_no: Yup.string()
    .trim()
    .max(25, "Branch GST number must be 25 characters or fewer")
    .required("Branch GST number is required"),
});

const AdminVerificationSchema = Yup.object().shape({
  first_name: Yup.string()
    .trim()
    .max(25, "First name must be 25 characters or fewer")
    .required("First name is required"),
  last_name: Yup.string()
    .trim()
    .max(25, "Last name must be 25 characters or fewer")
    .required("Last name is required"),
  company_state: Yup.string()
    .trim()
    .max(20, "Company state must be 20 characters or fewer")
    .required("Company state is required"),
  company_address: Yup.string()
    .trim()
    .max(200, "Company address must be 200 characters or fewer")
    .required("Company address is required"),
  company_gst_no: Yup.string()
    .trim()
    .max(25, "GST number must be 25 characters or fewer")
    .required("GST number is required"),
  password: Yup.string()
    .trim()
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&^#()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
      "Password must be at least 8 characters and include uppercase, lowercase, number, and special character"
    )
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Confirm password is required"),
  branches: Yup.array().of(branchSchema),
});

const showFieldError = (errors, touched, submitCount, fieldName) =>
  errors[fieldName] && (touched[fieldName] || submitCount > 0);

const showBranchFieldError = (errors, touched, submitCount, index, fieldName) =>
  errors?.branches?.[index]?.[fieldName] &&
  (touched?.branches?.[index]?.[fieldName] || submitCount > 0);

const emptyBranch = () => ({
  branch_name: "",
  branch_address: "",
  branch_state: "",
  branch_gst_no: "",
});

const AdminVerification = () => {
  const { uid } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchAdminDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axiosInstance.get(`/api/admin/verify/${uid}`);

        if (response.data?.status) {
          setAdminData(response.data.data || {});
        } else {
          const msg =
            response.data?.message || "Failed to fetch admin details.";
          toast.error(msg);
          setError(msg);
        }
      } catch (err) {
        console.error("Error fetching admin details:", err);
        const msg =
          err.response?.data?.message ||
          "An error occurred while fetching admin details. Please contact support@tag8.co";
        toast.error(msg);
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    if (uid) fetchAdminDetails();
  }, [uid]);

  const logoUrl =
    "https://storage.googleapis.com/pettag/qr/assets/Tag8-Logo-slst--black.png";

  const header = (
    <Navbar color="light" light expand="md" className="mb-3">
      <Container>
        <NavbarBrand href="/">
          <img src={logoUrl} alt="tag8" style={{ height: 36 }} />
        </NavbarBrand>
      </Container>
    </Navbar>
  );

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      const updatePayload = {
        company_state: values.company_state,
        company_address: values.company_address,
        company_gst_no: values.company_gst_no,
        password: values.password,
        first_name: values.first_name,
        last_name: values.last_name,
        branches: values.branches,
      };

      const response = await axiosInstance.post(
        `/api/admin/verify/${uid}`,
        updatePayload
      );

      if (response.data?.status) {
        setSuccess(true);
        toast.success(
          response.data.message ||
            "Account verified and password set successfully!"
        );
        setTimeout(() => history.push("/login"), 3000);
      } else {
        const msg = response.data?.message || "Failed to update admin details.";
        toast.error(msg);
      }
    } catch (err) {
      console.error("Error updating admin details:", err);
      const msg =
        err.response?.data?.message ||
        "An error occurred while updating admin details.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const noOfBranches = Number(adminData?.no_of_branches) || 0;

  if (loading) {
    return (
      <div>
        {header}
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col md="8" className="text-center">
              <Spinner color="primary" />
              <p className="mt-3">Loading admin details...</p>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        {header}
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col md="8">
              <Alert color="danger">
                <p className="mb-1">{error}</p>
                <p className="mb-0">
                  If the problem persists, please contact{" "}
                  <a href="mailto:support@tag8.co">support@tag8.co</a>
                </p>
              </Alert>
              <Button
                color="secondary"
                onClick={() => history.push("/")}
                className="w-100"
              >
                Go to Home
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (success) {
    return (
      <div>
        {header}
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col md="8">
              <Alert color="success">
                <h4>Account Verified Successfully!</h4>
                <p>Your account has been verified and password has been set.</p>
                <p className="mb-0">Redirecting to login...</p>
              </Alert>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div>
      {header}
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md="9" lg="8">
            <h4 className="mb-1">Verify Your Account & Set Password</h4>
            <p className="text-muted mb-4">
              Review your account details, complete company and branch
              information, and set your password
            </p>

            {adminData && (
              <Formik
                enableReinitialize
                initialValues={{
                  email: adminData.email || "",
                  company_name: adminData.name || adminData.company_name || "",
                  company_phonenumber:
                    adminData.phone || adminData.company_phonenumber || "",
                  no_of_branches: noOfBranches,
                  company_state: adminData.company_state || "",
                  company_address: adminData.company_address || "",
                  company_gst_no: adminData.company_gst_no || "",
                  first_name: "",
                  last_name: "",
                  password: "",
                  confirmPassword: "",
                  branches: Array.from({ length: noOfBranches }, () =>
                    emptyBranch()
                  ),
                }}
                validationSchema={AdminVerificationSchema}
                onSubmit={handleSubmit}
              >
                {({ errors, touched, submitCount, values }) => (
                  <Form>
                    <h5 className="mb-3">Account Details</h5>

                    <Row>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">Email</Label>
                          <Field
                            name="email"
                            type="email"
                            className="form-control"
                            disabled
                          />
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">Company Name</Label>
                          <Field
                            name="company_name"
                            type="text"
                            className="form-control"
                            disabled
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">First Name *</Label>
                          <Field
                            name="first_name"
                            type="text"
                            maxLength="25"
                            className={
                              "form-control" +
                              (showFieldError(
                                errors,
                                touched,
                                submitCount,
                                "first_name"
                              )
                                ? " is-invalid"
                                : "")
                            }
                          />
                          {showFieldError(
                            errors,
                            touched,
                            submitCount,
                            "first_name"
                          ) && (
                            <div className="invalid-feedback d-block">
                              {errors.first_name}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">Last Name *</Label>
                          <Field
                            name="last_name"
                            type="text"
                            maxLength="50"
                            className={
                              "form-control" +
                              (showFieldError(
                                errors,
                                touched,
                                submitCount,
                                "last_name"
                              )
                                ? " is-invalid"
                                : "")
                            }
                          />
                          {showFieldError(
                            errors,
                            touched,
                            submitCount,
                            "last_name"
                          ) && (
                            <div className="invalid-feedback d-block">
                              {errors.last_name}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">
                            Company Phone Number
                          </Label>
                          <Field name="company_phonenumber">
                            {({ field }) => (
                              <div className="input-group">
                                <span className="input-group-text">+91</span>
                                <input
                                  {...field}
                                  type="text"
                                  className="form-control"
                                  disabled
                                />
                              </div>
                            )}
                          </Field>
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">No. of Branches</Label>
                          <Field
                            name="no_of_branches"
                            type="text"
                            className="form-control"
                            disabled
                          />
                        </div>
                      </Col>
                    </Row>

                    <h5 className="mb-3 mt-4">Company Details</h5>
                    <Row>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">Company State *</Label>
                          <Field
                            name="company_state"
                            type="text"
                            maxLength="50"
                            className={
                              "form-control" +
                              (showFieldError(
                                errors,
                                touched,
                                submitCount,
                                "company_state"
                              )
                                ? " is-invalid"
                                : "")
                            }
                          />
                          {showFieldError(
                            errors,
                            touched,
                            submitCount,
                            "company_state"
                          ) && (
                            <div className="invalid-feedback d-block">
                              {errors.company_state}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">GST No. *</Label>
                          <Field
                            name="company_gst_no"
                            type="text"
                            maxLength="50"
                            className={
                              "form-control" +
                              (showFieldError(
                                errors,
                                touched,
                                submitCount,
                                "company_gst_no"
                              )
                                ? " is-invalid"
                                : "")
                            }
                          />
                          {showFieldError(
                            errors,
                            touched,
                            submitCount,
                            "company_gst_no"
                          ) && (
                            <div className="invalid-feedback d-block">
                              {errors.company_gst_no}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="12">
                        <div className="mb-3">
                          <Label className="form-label">
                            Company Address *
                          </Label>
                          <Field
                            name="company_address"
                            as="textarea"
                            rows="2"
                            maxLength="500"
                            className={
                              "form-control" +
                              (showFieldError(
                                errors,
                                touched,
                                submitCount,
                                "company_address"
                              )
                                ? " is-invalid"
                                : "")
                            }
                          />
                          {showFieldError(
                            errors,
                            touched,
                            submitCount,
                            "company_address"
                          ) && (
                            <div className="invalid-feedback d-block">
                              {errors.company_address}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    {noOfBranches > 0 && (
                      <FieldArray name="branches">
                        {() => (
                          <>
                            {values.branches.map((branch, index) => (
                              <div
                                key={index}
                                className="border rounded p-3 mb-4 mt-4"
                              >
                                <h5 className="mb-3">
                                  Branch {index + 1} Details
                                </h5>
                                <Row>
                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="form-label">
                                        Branch Name *
                                      </Label>
                                      <Field
                                        name={`branches.${index}.branch_name`}
                                        type="text"
                                        maxLength="50"
                                        className={
                                          "form-control" +
                                          (showBranchFieldError(
                                            errors,
                                            touched,
                                            submitCount,
                                            index,
                                            "branch_name"
                                          )
                                            ? " is-invalid"
                                            : "")
                                        }
                                      />
                                      {showBranchFieldError(
                                        errors,
                                        touched,
                                        submitCount,
                                        index,
                                        "branch_name"
                                      ) && (
                                        <div className="invalid-feedback d-block">
                                          {errors.branches[index].branch_name}
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="form-label">
                                        Branch Address *
                                      </Label>
                                      <Field
                                        name={`branches.${index}.branch_address`}
                                        as="textarea"
                                        rows="1"
                                        maxLength="500"
                                        className={
                                          "form-control" +
                                          (showBranchFieldError(
                                            errors,
                                            touched,
                                            submitCount,
                                            index,
                                            "branch_address"
                                          )
                                            ? " is-invalid"
                                            : "")
                                        }
                                      />
                                      {showBranchFieldError(
                                        errors,
                                        touched,
                                        submitCount,
                                        index,
                                        "branch_address"
                                      ) && (
                                        <div className="invalid-feedback d-block">
                                          {
                                            errors.branches[index]
                                              .branch_address
                                          }
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                </Row>

                                <Row>
                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="form-label">
                                        Branch State *
                                      </Label>
                                      <Field
                                        name={`branches.${index}.branch_state`}
                                        type="text"
                                        maxLength="50"
                                        className={
                                          "form-control" +
                                          (showBranchFieldError(
                                            errors,
                                            touched,
                                            submitCount,
                                            index,
                                            "branch_state"
                                          )
                                            ? " is-invalid"
                                            : "")
                                        }
                                      />
                                      {showBranchFieldError(
                                        errors,
                                        touched,
                                        submitCount,
                                        index,
                                        "branch_state"
                                      ) && (
                                        <div className="invalid-feedback d-block">
                                          {errors.branches[index].branch_state}
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="form-label">
                                        Branch GST No. *
                                      </Label>
                                      <Field
                                        name={`branches.${index}.branch_gst_no`}
                                        type="text"
                                        maxLength="50"
                                        className={
                                          "form-control" +
                                          (showBranchFieldError(
                                            errors,
                                            touched,
                                            submitCount,
                                            index,
                                            "branch_gst_no"
                                          )
                                            ? " is-invalid"
                                            : "")
                                        }
                                      />
                                      {showBranchFieldError(
                                        errors,
                                        touched,
                                        submitCount,
                                        index,
                                        "branch_gst_no"
                                      ) && (
                                        <div className="invalid-feedback d-block">
                                          {errors.branches[index].branch_gst_no}
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            ))}
                          </>
                        )}
                      </FieldArray>
                    )}

                    <h5 className="mb-3 mt-4">Set Password</h5>
                    <Row>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">Password *</Label>
                          <Field
                            name="password"
                            type="password"
                            className={
                              "form-control" +
                              (showFieldError(
                                errors,
                                touched,
                                submitCount,
                                "password"
                              )
                                ? " is-invalid"
                                : "")
                            }
                          />
                          {showFieldError(
                            errors,
                            touched,
                            submitCount,
                            "password"
                          ) && (
                            <div className="invalid-feedback d-block">
                              {errors.password}
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="mb-3">
                          <Label className="form-label">
                            Confirm Password *
                          </Label>
                          <Field
                            name="confirmPassword"
                            type="password"
                            className={
                              "form-control" +
                              (showFieldError(
                                errors,
                                touched,
                                submitCount,
                                "confirmPassword"
                              )
                                ? " is-invalid"
                                : "")
                            }
                          />
                          {showFieldError(
                            errors,
                            touched,
                            submitCount,
                            "confirmPassword"
                          ) && (
                            <div className="invalid-feedback d-block">
                              {errors.confirmPassword}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row className="mt-4">
                      <Col>
                        <div className="text-end">
                          <button
                            type="submit"
                            className="btn btn-success"
                            disabled={submitting}
                          >
                            {submitting ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Updating...
                              </>
                            ) : (
                              "Update & Verify Account"
                            )}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AdminVerification;
